import { useState, useMemo } from "react";
import { Loader2, ChevronLeft } from "lucide-react";
import { LIKERT_OPTIONS } from "@/data/questionnaires";

const ITEMS_PER_PAGE = 5;

/** Paged Likert questionnaire. Advances automatically once every item on a page is answered. */
export const Assessment = ({ items, eyebrow, title, intro, submitting, onComplete, testidPrefix }) => {
  const [page, setPage] = useState(0);
  const [responses, setResponses] = useState({});

  const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);
  const pageItems = useMemo(() => items.slice(page * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE + ITEMS_PER_PAGE), [items, page]);
  const answered = Object.keys(responses).length;
  const progress = Math.round((answered / items.length) * 100);

  const answer = (itemId, value) => {
    const next = { ...responses, [itemId]: value };
    setResponses(next);
    const pageComplete = pageItems.every((i) => next[i.id] !== undefined);
    if (!pageComplete) return;
    setTimeout(() => {
      if (page < totalPages - 1) setPage((p) => p + 1);
      else onComplete(next);
    }, 320);
  };

  return (
    <div data-testid={`${testidPrefix}-assessment`}>
      <div className="mb-10">
        <p className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] mb-3">{eyebrow}</p>
        <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">{title}</h1>
        <p className="text-slate-400 text-sm max-w-2xl leading-relaxed">{intro}</p>
      </div>

      <div className="mb-10">
        <div className="flex items-center justify-between text-xs uppercase tracking-widest text-slate-500 mb-3">
          <span data-testid={`${testidPrefix}-progress-label`}>
            Question {Math.min(answered + 1, items.length)} of {items.length}
          </span>
          <span>{progress}%</span>
        </div>
        <div className="h-[3px] bg-slate-800">
          <div
            className="h-full bg-[#D4AF37] transition-all duration-500"
            style={{ width: `${progress}%` }}
            data-testid={`${testidPrefix}-progress-bar`}
          />
        </div>
      </div>

      <div className="space-y-5">
        {pageItems.map((item, idx) => (
          <div
            key={item.id}
            className="border border-slate-800 bg-[#0B1220]/80 p-5 md:p-7"
            style={{ animation: `fadeInUp 0.35s ease-out ${idx * 60}ms both` }}
            data-testid={`${testidPrefix}-item-${item.id}`}
          >
            <p className="text-white text-base md:text-lg mb-5 leading-snug">{item.text}</p>
            <div className="grid grid-cols-5 gap-2">
              {LIKERT_OPTIONS.map((opt) => {
                const selected = responses[item.id] === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => answer(item.id, opt.value)}
                    data-testid={`${testidPrefix}-${item.id}-option-${opt.value}`}
                    className={`group py-3 px-1 border text-center transition-all duration-200 ${
                      selected
                        ? "border-[#D4AF37] bg-[#D4AF37]/15"
                        : "border-slate-800 hover:border-slate-600 hover:bg-slate-900/60"
                    }`}
                  >
                    <span className={`block text-lg font-medium ${selected ? "text-[#D4AF37]" : "text-slate-300"}`}>
                      {opt.short}
                    </span>
                    <span className="hidden md:block text-[10px] uppercase tracking-wider mt-1 text-slate-500 leading-tight">
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          data-testid={`${testidPrefix}-prev-btn`}
          className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 hover:text-white disabled:opacity-30 disabled:hover:text-slate-500 transition-colors"
        >
          <ChevronLeft size={14} /> Previous
        </button>
        {submitting && (
          <span className="flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-widest" data-testid={`${testidPrefix}-submitting`}>
            <Loader2 className="animate-spin" size={14} /> Scoring
          </span>
        )}
        <span className="text-xs uppercase tracking-widest text-slate-600">
          Page {page + 1} / {totalPages}
        </span>
      </div>
    </div>
  );
};

export default Assessment;
